import EmployeeTable from "./ui/EmployeeTable"
import { useNavigate } from "react-router-dom";
import { useEmployees } from "./modal/EmployeesContext";
import { fetchEmployees, fetchEmployeesForEmployee } from "./api/employeesApi";
import { fetchAvailableRoles } from "features/roles/api/RolesApi";
import { UserPlus, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { VerifyContext } from "app/VerifyContext";
import SearchInput from "shared/SearchInput";

const PAGE_SIZES = [10, 25, 50];

const EmployeesBody = () => {
    const { setEditingEmployee, setEmployeesList, setRolesAvailable } = useEmployees()
    const { authUserType, superAdmin, user } = useContext(VerifyContext);
    const employeeMode = authUserType === "employee" && !superAdmin;
    const navigate = useNavigate()

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [statusFilter, setStatusFilter] = useState("active");
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [tmOnly, setTmOnly] = useState(false);
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);

    const loadEmployees = useCallback(async (p: number, q: string, size?: number, status?: string, tmOnlyOverride?: boolean) => {
        setLoading(true);
        try {
            if (employeeMode && !user?.id) {
                throw new Error("Employee ID not found in session");
            }
            const data = employeeMode
              ? await fetchEmployeesForEmployee(user?.id || 0)
              : await fetchEmployees(p, size || pageSize, undefined, q || undefined, status ?? statusFilter, tmOnlyOverride ?? tmOnly);
            if (!mountedRef.current) return;
            setEmployeesList(data.employees);
            setTotalPages(data.totalPages);
            setTotalCount(data.totalCount);
            setPage(data.page);
        } catch (error) {
            if (!mountedRef.current) return;
            console.error("Failed to load employees:", error);
        }
        if (mountedRef.current) setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setEmployeesList, pageSize, statusFilter, tmOnly, employeeMode, user?.id]);

    useEffect(() => {
        loadEmployees(page, search);
        fetchAvailableRoles().then(setRolesAvailable).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        setSearch(searchInput);
        loadEmployees(1, searchInput);
    };

    const handleStatusFilter = (status: string) => {
        setStatusFilter(status);
        setStatusDropdownOpen(false);
        loadEmployees(1, search, pageSize, status);
    };

    const handleTmOnlyToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.checked;
        setTmOnly(next);
        loadEmployees(1, search, pageSize, statusFilter, next);
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setSearch("");
        loadEmployees(1, "");
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        loadEmployees(1, search, newSize);
    };

    const handlePrev = () => {
        if (page > 1) loadEmployees(page - 1, search);
    };

    const handleNext = () => {
        if (page < totalPages) loadEmployees(page + 1, search);
    };

    const registerEmployee = () => {
        setEditingEmployee(null)
        navigate("register-employees")
    }

    return (
        <div>
            {/* Page heading */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground font-inter">Employees</h1>
                    <p className="text-sm text-muted-foreground font-inter mt-1">Manage your team members and their information</p>
                </div>
                {!employeeMode && (
                    <button
                        type="button"
                        onClick={registerEmployee}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors whitespace-nowrap"
                    >
                        <UserPlus className="w-4 h-4" />
                        Register Employee
                    </button>
                )}
            </div>

            {/* Search bar + status filter */}
            <div className="flex items-center gap-3 mb-6">
                <SearchInput
                    value={searchInput}
                    onChange={setSearchInput}
                    onSearch={handleSearch}
                    onClear={handleClearSearch}
                    showClear={!!search}
                    placeholder="Search by name, code, email, or designation..."
                    className="flex-1"
                />
                {!employeeMode && (
                    <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none whitespace-nowrap">
                        <input
                            type="checkbox"
                            checked={tmOnly}
                            onChange={handleTmOnlyToggle}
                            className="rounded border-border text-primary focus:ring-ring"
                        />
                        TM only
                    </label>
                )}
                {!employeeMode && (
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-surface-muted border border-border hover:bg-surface-muted text-foreground text-sm font-medium font-inter rounded-xl transition-colors min-w-[140px] justify-between"
                        >
                            {statusFilter === "active" ? "Active" : statusFilter === "inactive" ? "Inactive" : "All"}
                            <ChevronDown className={`w-4 h-4 transition-transform ${statusDropdownOpen ? "rotate-180" : ""}`} />
                        </button>
                        {statusDropdownOpen && (
                            <div className="absolute right-0 top-full mt-1 z-50 w-full bg-surface-muted border border-border rounded-xl overflow-hidden shadow-xl">
                                {[
                                    { value: "active", label: "Active", color: "text-emerald-400" },
                                    { value: "inactive", label: "Inactive", color: "text-amber-400" },
                                    { value: "all", label: "All", color: "text-foreground" },
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => handleStatusFilter(opt.value)}
                                        className={`w-full text-left px-4 py-2.5 text-sm font-inter transition-colors border-b border-border last:border-0 ${
                                            statusFilter === opt.value ? `${opt.color} bg-primary/10` : "text-foreground hover:bg-surface-muted"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Loading overlay */}
            {loading && (
                <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            )}

            {/* Table */}
            {!loading && <EmployeeTable />}

            {/* Pagination */}
            {!loading && totalCount > 0 && !employeeMode && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                    <div className="flex items-center gap-4">
                        <p className="text-sm text-muted-foreground font-inter">
                            Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                        </p>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground font-inter">Per page:</span>
                            {PAGE_SIZES.map((size) => (
                                <button
                                    key={size}
                                    onClick={() => handlePageSizeChange(size)}
                                    className={`px-2 py-1 text-xs rounded font-inter transition-colors ${
                                        size === pageSize
                                            ? "bg-primary text-primary-foreground"
                                            : "text-muted-foreground hover:text-foreground hover:bg-surface-muted"
                                    }`}
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>
                    {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={handlePrev}
                            disabled={page <= 1}
                            title="Previous page"
                            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-muted disabled:text-muted-foreground disabled:hover:bg-transparent transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <button
                                key={p}
                                onClick={() => loadEmployees(p, search)}
                                className={`w-8 h-8 rounded-lg text-sm font-inter transition-colors ${
                                    p === page
                                        ? "bg-primary text-primary-foreground"
                                        : "text-muted-foreground hover:text-foreground hover:bg-surface-muted"
                                }`}
                            >
                                {p}
                            </button>
                        ))}

                        <button
                            onClick={handleNext}
                            title="Next page"
                            disabled={page >= totalPages}
                            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-muted disabled:text-muted-foreground disabled:hover:bg-transparent transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                    )}
                </div>
            )}

            {/* Empty state */}
            {!loading && totalCount === 0 && (
                <div className="text-center py-12">
                    <p className="text-muted-foreground font-inter text-sm">
                        {search ? `No employees found for "${search}"` : "No employees yet"}
                    </p>
                </div>
            )}
        </div>
    )
}

export default EmployeesBody
